import {
  onCleanup,
  createMemo,
  batch,
  createRoot,
  createEffect,
  on,
  onMount,
  createSignal,
} from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/+esm';
import { insert } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/web/+esm';
import { register, compose } from 'https://cdn.jsdelivr.net/npm/component-register@0.6.7/+esm';

// Utility custom element functions

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner) return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (next && !next._$owner && !(next.assignedSlot && next.assignedSlot._$owner)) next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = '';
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.renderRoot = element;
    return ComponentType(props, options);
  };
}

const customShadowlessElement = (tagName, props = {}, Component = (props, options) => {}, ...rest) =>
  compose(register(tagName, props), withNoShadowDOM, withSolid, ...rest)(Component);

function findVariantByOption(product, option) {
  const optionIndex = product.options.findIndex((option) => option === 'Color') + 1;
  return product.variants.find((variant) => variant[`option${optionIndex}`] === option);
}
function parseInitialImageParams(imageUrl) {
  const url = new URL(`https:${imageUrl}`);
  return {
    width: url.searchParams.get('width') || 0,
    height: url.searchParams.get('height') || 0,
    crop: url.searchParams.get('crop'),
  };
}

function sizeImageUrl(imageUrl, { width = 0, height = 0, crop = null } = { width: 0, height: 0, crop: null }) {
  const url = new URL(`http:${imageUrl}`);
  if (width) url.searchParams.set('width', width);
  if (height) url.searchParams.set('height', height);
  if (crop) url.searchParams.set('crop', crop);
  return url.toString();
}

function buildSrcSetFromPrevious(image, imageUrl) {
  const srcsets = image.srcset.split(', ');
  return srcsets
    .map((srcset) => {
      const [url, bp] = srcset.split(' ');
      const params = parseInitialImageParams(url);
      const sizedImage = sizeImageUrl(imageUrl, params);
      return `${sizedImage} ${bp}`;
    })
    .join(', ');
}

function handleImageChange(images, { variant, product }) {
  if (!variant) return;
  images.forEach((image) => {
    const variantImageSrc = product.images[variant.featured_image.position - 1];
    const params = parseInitialImageParams(image.src);
    const imageSrc = sizeImageUrl(variantImageSrc, params);
    const imageSrcSet = buildSrcSetFromPrevious(image, variantImageSrc);
    image.setAttribute('src', imageSrc);
    image.setAttribute('srcset', imageSrcSet);
  });
}

function updateInputId(inputId, variantId) {
  if (!variantId) return;
  inputId.value = variantId;
  inputId.dispatchEvent(new Event('change', { bubbles: true }));
}

function getColorInfo(element) {
  const input = element.querySelector('input[name="Color"]:checked');
  return JSON.parse(input.getAttribute('data-color'));
}

function updatePrice(price, variant) {
  if (!variant) return;
  const removeZeroes = price.getAttribute('data-without_trailing_zeroes') === 'true';
  const regularPrice = price.querySelectorAll('[data-price]');
  const comparePrice = price.querySelectorAll('[data-compare_at_price]');

  const regularPriceBlock = price.querySelectorAll('.price__regular');
  const salePriceBlock = price.querySelectorAll('.price__sale');

  let format = Shopify.money_format;
  if (removeZeroes) {
    format = format.replace('amount', 'amount_no_decimals');
  }

  regularPrice.forEach((el) => {
    el.textContent = Shopify.formatMoney(variant.price, format);
  });

  comparePrice.forEach((el) => {
    el.textContent = Shopify.formatMoney(variant.compare_at_price, format);
  });

  const isOnSale = variant.compare_at_price > variant.price;

  regularPriceBlock.forEach((el) => (isOnSale ? el.classList.add('hidden') : el.classList.remove('hidden')));
  salePriceBlock.forEach((el) => (isOnSale ? el.classList.remove('hidden') : el.classList.add('hidden')));
}

function updateColorName(colorName, colorInfo) {
  if (!colorInfo || !colorInfo.color_name) {
    return colorName.classList.add('hidden');
  }
  colorName.textContent = colorInfo.color_name;
  colorName.classList.remove('hidden');
}
function updateColorDescription(colorDescription, colorInfo, separator) {
  if (!colorInfo || !colorInfo?.description) {
    return colorDescription.classList.add('hidden');
  }
  colorDescription.textContent = separator.concat(colorInfo.description);
  colorDescription.classList.remove('hidden');
}

function hideColorInfo(elem, colorInfo, variant) {
  if (!colorInfo?.description && !variant?.title) {
    return elem.classList.add('hidden');
  }
  return elem.classList.remove('hidden');
}

function updateVariantTitle(target, variant) {
  if (!variant) return;
  target.textContent = variant.title;
}

function ColorSelector(props, { element }) {
  const [colorInfo, setColorInfo] = createSignal(getColorInfo(element));
  const product = JSON.parse(
    Array.from(element.children).find((child) => child.type === 'application/json').textContent
  );

  const selectedVariant = createMemo(() => {
    if (!props.selectedOption) return null;
    return findVariantByOption(product, props.selectedOption);
  });

  const root = element.closest(props.rootTarget);
  const images = root.querySelectorAll(props?.imageTarget || null);
  const price = root.querySelector(props?.priceTarget || null);
  const inputId = root.querySelector('input[name="id"]');
  const colorName = root.querySelector(props?.colorLabelTarget || null);
  const colorDescription = root.querySelector(props?.colorDescriptionTarget || null);
  const variantTitleTarget = root.querySelector(props?.variantTitleTarget || null);

  createEffect(
    on(
      () => ({ variant: selectedVariant(), shouldUpdateLinks: props.shouldUpdateLinks }),
      ({ variant, shouldUpdateLinks }) => {
        if (!variant || !shouldUpdateLinks) return;
        const links = root.querySelectorAll('a[href*="products"]');
        links.forEach((link) => {
          const url = new URL(`${window.location.origin}${link.getAttribute('href')}`);
          url.searchParams.set('variant', variant.id);
          link.setAttribute('href', `${url.pathname}${url.search}`);
        });
      }
    )
  );

  createEffect(
    on(
      () => ({ variant: selectedVariant(), shouldUpdateUrl: props.shouldUpdateUrl }),
      ({ variant, shouldUpdateUrl }) => {
        if (!variant || !shouldUpdateUrl) return;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.pushState({ ...history.state, variant: variant.id }, null, url);
      }
    )
  );

  if (colorName) {
    createEffect(() => {
      updateColorName(colorName, colorInfo());
    });
  }

  if (colorDescription) {
    createEffect(() => {
      updateColorDescription(colorDescription, colorInfo(), props.colorSeparator);
    });
  }

  if (price) {
    createEffect(() => {
      updatePrice(price, selectedVariant());
    });
  }

  if (images.length) {
    createEffect(() => {
      handleImageChange(images, { variant: selectedVariant(), product });
    });
  }

  if (inputId) {
    createEffect(() => {
      updateInputId(inputId, selectedVariant()?.id);
    });
  }

  if (variantTitleTarget) {
    createEffect(() => {
      updateVariantTitle(variantTitleTarget, selectedVariant());
    });
  }

  onMount(() => {
    function updateSelectedOption(e) {
      if (e.target.tagName !== 'INPUT') return;
      if (e.target.type !== 'radio') return;
      if (e.target.name !== 'Color') return;
      batch(() => {
        element.setAttribute('selected-option', e.target.value);
        setColorInfo(getColorInfo(element));
      });
    }

    element.addEventListener('change', updateSelectedOption);

    onCleanup(() => {
      element.removeEventListener('change', updateSelectedOption);
    });
  });
}

customShadowlessElement(
  'color-selector',
  {
    selectedOption: '',
    imageTarget: '',
    colorLabelTarget: '',
    formId: '',
    rootTarget: '',
    priceTarget: '',
    colorDescriptionTarget: '',
    variantTitleTarget: '',
    colorSeparator: '',
    shouldUpdateLinks: false,
    shouldUpdateUrl: false,
  },
  ColorSelector
);
